import { isAfter, subHours } from 'date-fns';
import _get from 'lodash/get';
import { PRODUCT_PAYMENT_MAX_AMOUNT, TIME_SLOT_LENGTH } from '../../utils/global';
import { translate } from '../../utils/localization';
import { priceAsString } from '../../utils/price';
import {
  email,
  isBoolean,
  maxLength,
  minLength,
  numeric,
  numericMin,
  required,
  validateMaxAmount,
  validateMinAmount,
} from '../../utils/validation';

export const PersonSchema = {
  first_name: value =>
    required(
      value,
      translate('form.fields.default.required', { field: translate('form.fields.user.first_name.label') })
    ),
  last_name: value =>
    required(
      value,
      translate('form.fields.default.required', { field: translate('form.fields.user.last_name.label') })
    ),
  phone: [
    value =>
      required(value, translate('form.fields.default.required', { field: translate('form.fields.user.phone.label') })),
    value => minLength(value, 10, translate('form.fields.user.phone.valid')),
    value => maxLength(value, 14, translate('form.fields.user.phone.valid')),
    value => numeric(value, translate('form.fields.user.phone.valid')),
  ],
  email: [
    value =>
      required(value, translate('form.fields.default.required', { field: translate('form.fields.user.email.label') })),
    value => email(value, translate('form.fields.user.email.valid')),
  ],
};

export const productPaymentSchema = {
  amount: [
    value =>
      required(
        value,
        translate('form.fields.default.required', {
          field: translate('request_flow.product_payment.fields.amount.label'),
        })
      ),
    value =>
      validateMaxAmount(
        value,
        PRODUCT_PAYMENT_MAX_AMOUNT / 100, // amount is in cents, but users thinks in euro's
        translate('request_flow.product_payment.fields.amount.valid', {
          amount: priceAsString({ price: { amount: PRODUCT_PAYMENT_MAX_AMOUNT }, addPlusMinusIndicator: false }),
        })
      ),
    value =>
      validateMinAmount(
        value,
        1, // amount is 1 euro
        translate('request_flow.product_payment.fields.amount.valid_min', {
          amount: priceAsString({ price: { amount: 100 }, addPlusMinusIndicator: false }),
        })
      ),
  ],
};

export const itemSchema = {
  title: value =>
    required(
      value,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.title.label') })
    ),
  width: value =>
    numericMin(
      value,
      1,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.width.label') })
    ),
  height: value =>
    numericMin(
      value,
      1,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.height.label') })
    ),
  length: value =>
    numericMin(
      value,
      1,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.length.label') })
    ),
  weigth: value =>
    numericMin(
      value,
      0,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.weight.label') })
    ),
  count: value =>
    numericMin(
      value,
      1,
      translate('form.fields.default.required', { field: translate('request_flow.what.fields.count.label') })
    ),
  extraCare: value => required(value, translate('request_flow.fields.extra_care.valid')),
  productSelectionGroup: value => required(value, translate('request_flow.actions.make_choice')),
  productSelectionQuestion: value => isBoolean(value, translate('request_flow.actions.make_choice')),
};

export const pickupDeliverySchema = {
  pickup_time_start: (value?: string) => {
    return required(
      value,
      translate('form.fields.default.required', {
        field: translate('request_flow.time.fields.time_start.label'),
      })
    );
  },
  pickup_time_end: (value?: string, timeStart?: string) => {
    if (!timeStart) {
      return translate('request_flow.time.fields.select_time_start_first');
    }
    if (value) {
      const pickupStartDate = new Date(timeStart);
      const pickupEndDate = subHours(new Date(value), TIME_SLOT_LENGTH);
      if (isAfter(pickupStartDate, pickupEndDate)) {
        return translate('request_flow.time.fields.time_slot', {
          hours: TIME_SLOT_LENGTH,
        });
      }
    }
    return required(
      value,
      translate('form.fields.default.required', {
        field: translate('request_flow.time.fields.time_end.label'),
      })
    );
  },
};

export const getCountryCode = (allValues): string => {
  const addresPrefix = window.location.href.indexOf('delivery') > 0 ? 'delivery.address.' : 'pickup.address.';
  return _get(allValues, addresPrefix + 'country_code', '');
};

// Normalizer functions
export const parseInteger = (input: string): number | '' => {
  if (input === '0') {
    return '';
  }
  input = input.toString().replace('.', '');
  return parseInt(input.replace(/[^0-9.]/g, ''), 10);
};

export const numbersOnly = (input: string): string | undefined => {
  const stripped = input.replace(/[^\d.-.,]/g, '').trim();
  return stripped || undefined;
};
